/**
 * @fileoverview Edit User choices query hook.
 * @description Wraps GET /users/{userId}/edit-options for the Edit User form: the primary Sales
 * roles the caller may give this user, and the reporting managers the hierarchy accepts for the
 * chosen role. The backend only offers these for an ACTIVE user, so the caller passes `enabled`
 * false for anyone else.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useQuery } from '@tanstack/react-query'

import { USERS_QUERY_KEYS } from '@/features/users/constants/users-query-keys.constants'
import {
  getUserManagerEditOptions,
  getUserRoleEditOptions,
} from '@/features/users/services/users.service'

import type { ReportingManagerOption, RoleOption } from '@/features/users/types/users.types'
import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Fetches the role choices, and the manager choices for the chosen role.
 * @param {string} userId - The user being edited.
 * @param {string} primaryRoleId - The chosen role, or '' before one is chosen.
 * @param {boolean} enabled - Whether role and manager can be edited at all.
 * @returns {object} The role and manager queries.
 */
export function useUserEditOptions(userId: string, primaryRoleId: string, enabled: boolean) {
  const rolesQuery = useQuery<RoleOption[], ApiError>({
    queryKey: [...USERS_QUERY_KEYS.editOptions(userId), 'ROLE'],
    queryFn: () => getUserRoleEditOptions(userId),
    enabled,
  })
  const managersQuery = useQuery<ReportingManagerOption[], ApiError>({
    queryKey: [...USERS_QUERY_KEYS.editOptions(userId), 'REPORTING_MANAGER', primaryRoleId],
    queryFn: () => getUserManagerEditOptions(userId, primaryRoleId),
    enabled: enabled && primaryRoleId !== '',
  })

  return { rolesQuery, managersQuery }
}
